import moment from "moment/min/moment-with-locales";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Col, Row, Stack, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { Await, useRouteLoaderData } from "react-router-dom";
import DataDescription from "../components/DataDescription";
import { getDemoName, getRadioName } from "../components/HelperFunctions";
import {
    useFetchData,
    useLatestCalculatedDate,
    useResolvedRadios,
    useSetDaypartOnChange,
    useSetDaysOnPerspectiveChange,
    useSetPerspectiveOnDaysChange,
} from "../components/HelperHooks";
import LoadingIndicator from "../components/LoadingIndicator";
import PageDesciption from "../components/PageDesciption";
import DatesPicker, {
    latest2Weeks,
    latestFull2Weeks,
    latestFull4Weeks,
    latestFullWeek,
    latestMonth,
    latestQuarter,
    latestWeek,
    latestYear,
} from "../components/Pickers/DatesPicker";
import DaypartPicker from "../components/Pickers/DaypartPicker";
import DaysPicker from "../components/Pickers/DaysPicker";
import DemoPicker from "../components/Pickers/DemoPicker";
import HoursPicker from "../components/Pickers/HoursPicker";
import MinutesPicker from "../components/Pickers/MinutesPicker";
import PerspectivePicker from "../components/Pickers/PerspectivePicker";
import StationPicker from "../components/Pickers/StationPicker";
import RatingsChart from "../components/RatingsChart";
import { useUserFormState } from "../components/UserSelection";
import { DataContent, DescriptionWithDownloadRow, InputSelection, ModulePage } from "../components/pageSections";
import RATooltip from "../components/RATooltip";

const extraStationsCount = 3;
const extraDefaults = Object.assign(
    {},
    ...Array(extraStationsCount)
        .fill()
        .map((_, i) => ({ [`extraStation${i}`]: [], [`extraDemo${i}`]: [] })),
);

export default function Ratings() {
    const moduleName = useMemo(() => "ratings", []);
    const moduleId = useMemo(() => ({ 1: 3, 2: 19 }), []);
    const { demographics, userInfo, radiosModuleSettings } = useRouteLoaderData("root");

    const [formData, setFormData, saveFormData, loadFormData] = useUserFormState(moduleName, extraDefaults);

    const [latestCalculatedDate, setLatestCalculatedDate] = useState(null);
    const [lastYearAverages, setLastYearAverages] = useState(false);
    const [grouping, setGrouping] = useState(0);
    const [allowedGroupings, setAllowedGroupings] = useState([]);
    const [defaultGrouping, setDefaultGrouping] = useState(0);
    const [shares, setShares] = useState(false);

    const [loading, errorCode, data, fetchData] = useFetchData();
    const [subLoading, setSubLoading] = useState(false);

    const resolvedRadios = useResolvedRadios();
    const [resolvedDemos, setResolvedDemos] = useState(null);

    const container = useRef(null);
    const advancedInputRef = useRef(null);
    const [fileName, setFileName] = useState("Ratings");

    const predefinedRanges = useMemo(() => {
        if (latestCalculatedDate) {
            if (formData.dateRange === undefined) {
                setFormData(current => ({ ...current, dateRange: latestMonth(latestCalculatedDate, true) }));
            }
            return [
                latestWeek(latestCalculatedDate),
                latest2Weeks(latestCalculatedDate),
                latestMonth(latestCalculatedDate),
                latestQuarter(latestCalculatedDate),
                latestYear(latestCalculatedDate),
                latestFullWeek(latestCalculatedDate),
                latestFull2Weeks(latestCalculatedDate),
                latestFull4Weeks(latestCalculatedDate),
            ];
        }
    }, [formData.dateRange, latestCalculatedDate, setFormData]);

    useEffect(() => {
        async function resolveDemos() {
            setResolvedDemos((await demographics).data);
        }
        resolveDemos();
    }, [demographics]);

    const csvHeaders = useMemo(() => {
        if (!resolvedDemos || !resolvedRadios || !data?.data) return;

        let radioNames = data.data.map(x => getRadioName(x.radio.radio_id, resolvedRadios));
        const start = moment(data.start_datetime).format("DD-MM-YYYY");
        const end = moment(data.end_datetime).format("DD-MM-YYYY");

        setFileName(`Ratings ${radioNames.join(" and ")} from ${start} to ${end}`);

        const headers = [
            "Basis Datetime",
            ...data.data.map(x => `${getRadioName(x.radio.radio_id, resolvedRadios)} - ${getDemoName(x.radio.demo_id, resolvedDemos)}`),
        ].concat(`${getRadioName(data.data[0].radio.radio_id, resolvedRadios)} - ${getDemoName(data.data[0].radio.demo_id, resolvedDemos)} (AVG)`);

        return headers;
    }, [data, formData.dateRange, resolvedDemos, resolvedRadios]);

    const csvData = useMemo(() => {
        if (!data?.data) return;

        return data.data[0].ratings.map((x, i) => [x.basis_minute, ...data?.data.map(d => d.ratings[i].connections)].concat(data?.data[0].ratings[i].average));
    }, [data?.data]);

    useSetPerspectiveOnDaysChange(setFormData, formData.days);
    useSetDaysOnPerspectiveChange(setFormData, formData.perspective);
    useLatestCalculatedDate(setLatestCalculatedDate, moduleId, formData.station, formData.demo);
    useSetDaypartOnChange(setFormData, formData.station, formData.days, formData.hourRange);

    useEffect(() => {
        if (data?.start_datetime && data?.end_datetime) {
            const start = new Date(data.start_datetime);
            const end = new Date(data.end_datetime);
            const differenceMs = end.getTime() - start.getTime();
            const differenceDays = Math.ceil(differenceMs / (1000 * 60 * 60 * 24));

            if (differenceDays <= 1) {
                setAllowedGroupings([0, 1, 2]);
                setDefaultGrouping(0);
            } else if (differenceDays <= 7) {
                setAllowedGroupings([0, 1, 2, 3]);
                setDefaultGrouping(1);
            } else if (differenceDays <= 14) {
                setAllowedGroupings([1, 2, 3, 4]);
                setDefaultGrouping(1);
            } else if (differenceDays <= 31) {
                setAllowedGroupings([1, 2, 3, 4]);
                setDefaultGrouping(2);
            } else if (differenceDays <= 92) {
                setAllowedGroupings([1, 2, 3, 4, 5]);
                setDefaultGrouping(3);
            } else if (differenceDays <= 184) {
                setAllowedGroupings([3, 4, 5]);
                setDefaultGrouping(4);
            } else if (differenceDays <= 366) {
                setAllowedGroupings([3, 4, 5]);
                setDefaultGrouping(5);
            } else {
                setAllowedGroupings([3, 4, 5]);
                setDefaultGrouping(5);
            }
        } else {
            setAllowedGroupings([]);
        }
    }, [data]);

    const createRadioInput = (station, demo) => ({ radio_id: station?.radio_id, demo_id: demo?.demo_id });

    async function getData(lastYearAverage, grouping, onlySubLoading = false) {
        if (onlySubLoading) setSubLoading(true);
        const start_datetime = moment(formData.dateRange[0]).startOf("day").format("YYYY-MM-DDTHH:mm:ss");
        const end_datetime = moment.min([moment(formData.dateRange[1]).endOf("day"), moment(latestCalculatedDate)]).format("YYYY-MM-DDTHH:mm:ss");
        const radios = [
            createRadioInput(formData.station[0], formData.demo[0]),
            ...Array(extraStationsCount)
                .fill()
                .map((_, i) => createRadioInput(formData[`extraStation${i}`][0], formData[`extraDemo${i}`][0]))
                .filter(({ radio_id, demo_id }) => radio_id && demo_id),
        ];
        const input = {
            radios: radios,
            start_datetime: start_datetime,
            end_datetime: end_datetime,
            start_hour: formData.hourRange[0],
            end_hour: formData.hourRange[1] - 1,
            start_minute: formData.minuteRange[0],
            end_minute: formData.minuteRange[1] - 1,
            weekdays: formData.days,
            grouping: grouping,
            include_averages: true,
            last_year_as_averages: lastYearAverage,
        };
        await fetchData("/ratings/new/", input);
        if (onlySubLoading) setSubLoading(false);
    }

    async function handleFormSubmit(event) {
        event.preventDefault();
        await getData(lastYearAverages, defaultGrouping);
        advancedInputRef.current.closeAdvanced();
        setGrouping(defaultGrouping);
        await saveFormData();
        await saveFormData(formData.station[0].radio_id);
    }

    async function handleAverageChange(value) {
        setLastYearAverages(value);
        await getData(value, grouping, true);
    }
    async function handleGroupingChange(value) {
        setGrouping(value);
        await getData(lastYearAverages, value, true);
    }
    return (
        <ModulePage>
            <ModulePage.Title>Ratings</ModulePage.Title>
            <ModulePage.InputSelection
                handleFormSubmit={handleFormSubmit}
                disabled={
                    !formData.station.length ||
                    !formData.demo.length ||
                    !formData.dateRange ||
                    !formData.days.length ||
                    formData.hourRange[0] === formData.hourRange[1] ||
                    formData.minuteRange[0] === formData.minuteRange[1]
                }>
                <InputSelection.Main>
                    <Suspense>
                        <Await resolve={userInfo}>
                            {resolvedUser => (
                                <StationPicker
                                    formData={formData}
                                    setFormData={setFormData}
                                    name={moduleName}
                                    moduleFilterFunction={x => x.module_id === 3 || x.module_id === 19}
                                    filterFunction={x => resolvedUser.data.superuser || !x.mtt_only}
                                    loadOnChange={loadFormData}
                                />
                            )}
                        </Await>
                    </Suspense>
                    <Suspense>
                        <Await resolve={radiosModuleSettings}>
                            {resolvedModuleSettings => (
                                <DemoPicker
                                    formData={formData}
                                    setFormData={setFormData}
                                    name={moduleName}
                                    disabled={!formData.station.length}
                                    filterFunction={x => {
                                        if (!formData.station.length) return false;
                                        const station = formData.station[0];
                                        const hasListenerGroupModule = resolvedModuleSettings.data.find(
                                            r => r.radio_id === station.radio_id && r.module_id === 7 && r.enabled,
                                        );
                                        return (
                                            (x.data_type === 1 && station.stream && (hasListenerGroupModule ? true : x.listener_group === 0)) ||
                                            (x.data_type === 2 && station.ppm)
                                        );
                                    }}
                                />
                            )}
                        </Await>
                    </Suspense>
                    <DatesPicker formData={formData} setFormData={setFormData} predefinedRanges={predefinedRanges} latestAvailableDate={latestCalculatedDate} />
                    <DaypartPicker formData={formData} setFormData={setFormData} name={moduleName} />
                </InputSelection.Main>
                <Suspense>
                    <Await resolve={radiosModuleSettings}>
                        {resolvedModuleSettings => (
                            <InputSelection.Advanced moduleName={moduleName} ref={advancedInputRef}>
                                <DaysPicker formData={formData} setFormData={setFormData} name={moduleName} />
                                <HoursPicker formData={formData} setFormData={setFormData} name={moduleName} />
                                <MinutesPicker formData={formData} setFormData={setFormData} name={moduleName} />
                                <PerspectivePicker formData={formData} setFormData={setFormData} name={moduleName} />
                                {Array(extraStationsCount)
                                    .fill()
                                    .map((_, i) => {
                                        const station = formData[`extraStation${i}`][0];
                                        return (
                                            <Col xs={12} key={i}>
                                                <Row>
                                                    <StationPicker
                                                        formData={formData}
                                                        setFormData={setFormData}
                                                        name={moduleName}
                                                        moduleFilterFunction={x => x.module_id === 3 || x.module_id === 19}
                                                        formKey={`extraStation${i}`}
                                                        demoKey={`extraDemo${i}`}
                                                        label={`Station #${i + 2}`}
                                                    />

                                                    <DemoPicker
                                                        formData={formData}
                                                        setFormData={setFormData}
                                                        name={moduleName}
                                                        disabled={!formData[`extraStation${i}`].length || formData[`extraStation${i}`][0]?.mtt_only}
                                                        formKey={`extraDemo${i}`}
                                                        stationKey={`extraStation${i}`}
                                                        label={`Demographic #${i + 2}`}
                                                        filterFunction={x => {
                                                            if (!station) return false;
                                                            const hasListenerGroupModule = resolvedModuleSettings.data.find(
                                                                r => r.radio_id === station.radio_id && r.module_id === 7 && r.enabled,
                                                            );
                                                            return (
                                                                (x.data_type === 1 &&
                                                                    station.stream &&
                                                                    (hasListenerGroupModule ? true : x.listener_group === 0)) ||
                                                                (x.data_type === 2 && station.ppm)
                                                            );
                                                        }}
                                                    />
                                                </Row>
                                            </Col>
                                        );
                                    })}
                            </InputSelection.Advanced>
                        )}
                    </Await>
                </Suspense>
            </ModulePage.InputSelection>
            <ModulePage.DataContent loading={loading && !subLoading} errorCode={errorCode}>
                <DataContent.Placeholder show={!data}>
                    <PageDesciption
                        title={"Ratings"}
                        whatList={[
                            "Your daily listening as an easy to read graph",
                            "Choose different dayparts, timeframes, and listener groups",
                            "View multiple stations or listener groups in the same graph",
                        ]}
                        knowList={[
                            { text: "Grouping can be changed on the fly. Choose minutes, hours, days, weeks, or months with a click" },
                            { text: "Compare to last 13 weeks for short term trends, or last year’s average for long term development" },
                            { text: "Track single shows are days by choosing the options under “advanced”" },
                        ]}
                        relatedLinks={[
                            {
                                title: "An Apple a Day",
                                url: "https://radioanalyzer.com/blog/an-apple-a-day",
                            },
                        ]}
                        videoSrc="https://www.youtube.com/embed/qCjHAVycInE?si=_AoMoULxEeag-jLO"
                    />
                </DataContent.Placeholder>
                {data && (
                    <>
                        <div ref={container}>
                            <DescriptionWithDownloadRow csvHeaders={csvHeaders} data={csvData} filename={fileName} screenshotContainer={container}>
                                <DataDescription>
                                    <DataDescription.Stations radioIds={data?.radios.map(x => x.radio_id)} />
                                    <DataDescription.DateRange startDate={data?.start_datetime} endDate={data?.end_datetime} />
                                    <DataDescription.HourRange startHour={data?.start_hour} endHour={data?.end_hour} />
                                    <DataDescription.MinuteRange startMinute={data?.start_minute} endMinute={data?.end_minute} />
                                    <DataDescription.Perspective days={data?.weekdays} />
                                </DataDescription>
                            </DescriptionWithDownloadRow>
                            <Stack direction="horizontal" gap={1} className=" flex-wrap column-gap-4 row-gap-2 mb-2">
                                <Stack direction="horizontal" gap={2}>
                                    <span>
                                        Average
                                        <RATooltip>
                                            <p>
                                                <strong>13 weeks </strong>- Avg. of latest 13 weeks from the selected timeframe, holidays and corrupted days are
                                                excluded.
                                            </p>
                                            <p>
                                                <strong>Last year</strong> - The same weekday in the same calendar week of the previous year, in the HEALTH
                                                module, corrupted days are excluded
                                            </p>
                                        </RATooltip>
                                    </span>
                                    <ToggleButtonGroup
                                        type={"radio"}
                                        name="last-year-average"
                                        value={lastYearAverages}
                                        onChange={handleAverageChange}
                                        className="ra-switcher">
                                        <ToggleButton id={`${moduleName}-last-year-averages-off`} variant={"ra-dark-blue-outline"} value={false}>
                                            13 Weeks
                                        </ToggleButton>
                                        <ToggleButton
                                            id={`${moduleName}-last-year-averages-on`}
                                            className=" text-nowrap"
                                            variant={"ra-dark-blue-outline"}
                                            value={true}>
                                            Last Year
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Stack>
                                <Stack className="d-flex flex-md-row flex-column" gap={2}>
                                    <span className="justify-self-center align-self-md-center">Grouping</span>
                                    <ToggleButtonGroup
                                        type={"radio"}
                                        name="grouping"
                                        value={grouping}
                                        onChange={handleGroupingChange}
                                        className="ra-switcher flex-wrap flex-md-nowrap">
                                        <ToggleButton
                                            id={`${moduleName}-grouping-minutes`}
                                            variant={"ra-dark-blue-outline"}
                                            value={0}
                                            disabled={!allowedGroupings.includes(0)}>
                                            Minutes
                                        </ToggleButton>
                                        <ToggleButton
                                            id={`${moduleName}-grouping-hours`}
                                            variant={"ra-dark-blue-outline"}
                                            value={1}
                                            disabled={!allowedGroupings.includes(1)}>
                                            Hours
                                        </ToggleButton>
                                        <ToggleButton
                                            id={`${moduleName}-grouping-3-hours`}
                                            variant={"ra-dark-blue-outline"}
                                            value={2}
                                            disabled={!allowedGroupings.includes(2)}>
                                            3 Hours
                                        </ToggleButton>
                                        <ToggleButton
                                            id={`${moduleName}-grouping-days`}
                                            variant={"ra-dark-blue-outline"}
                                            value={3}
                                            disabled={!allowedGroupings.includes(3)}>
                                            Days
                                        </ToggleButton>
                                        <ToggleButton
                                            id={`${moduleName}-grouping-weeks`}
                                            variant={"ra-dark-blue-outline"}
                                            value={4}
                                            disabled={!allowedGroupings.includes(4)}>
                                            Weeks
                                        </ToggleButton>
                                        <ToggleButton
                                            id={`${moduleName}-grouping-months`}
                                            variant={"ra-dark-blue-outline"}
                                            value={5}
                                            disabled={!allowedGroupings.includes(5)}>
                                            Months
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Stack>
                                {data.radios.some(x => x.demo_id > 7) && (
                                    <Stack direction="horizontal" gap={2}>
                                        <span>Ratings</span>
                                        <ToggleButtonGroup type={"radio"} name="shares" value={shares} onChange={setShares} className="ra-switcher">
                                            <ToggleButton id={`${moduleName}-shares-off`} variant={"ra-dark-blue-outline"} value={false}>
                                                Cover
                                            </ToggleButton>
                                            <ToggleButton id={`${moduleName}-shares-on`} className=" text-nowrap" variant={"ra-dark-blue-outline"} value={true}>
                                                Shares
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </Stack>
                                )}
                            </Stack>
                            {subLoading ? (
                                <div className=" d-flex flex-grow-1">
                                    <LoadingIndicator width={75} />
                                </div>
                            ) : (
                                <RatingsChart
                                    data={data.data}
                                    grouping={grouping}
                                    resolvedRadios={resolvedRadios}
                                    resolvedDemos={resolvedDemos}
                                    ratingsType={shares && data.radios.some(x => x.demo_id > 7) ? "shares" : "connections"}
                                />
                            )}
                        </div>
                    </>
                )}
            </ModulePage.DataContent>
        </ModulePage>
    );
}
